import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  ArcElement, Tooltip, Legend
} from 'chart.js';
import { FiLayers, FiUsers, FiSend, FiMessageCircle } from 'react-icons/fi';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/superadmin/dashboard').then(({ data }) => { setStats(data); setLoading(false); })
      .catch(() => { toast.error('Failed to load dashboard'); setLoading(false); });
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><div className="spinner" /></div>;

  const portalStats = stats?.portalStats || [];

  // Doughnut - Portal status distribution
  const activePortals = portalStats.filter(p => p.status === 'active').length;
  const inactivePortals = portalStats.filter(p => p.status !== 'active').length;

  const doughnutData = {
    labels: ['Active', 'Inactive'],
    datasets: [{
      data: [activePortals || stats?.totalPortals || 0, inactivePortals],
      backgroundColor: ['#22c55e', '#ef4444'],
      borderColor: ['#FFFFFF', '#FFFFFF'],
      borderWidth: 3,
      hoverOffset: 6
    }]
  };

  const doughnutOptions = {
    responsive: true,
    cutout: '68%',
    plugins: {
      legend: { position: 'bottom', labels: { color: '#475569', font: { family: 'Inter, sans-serif', size: 12, weight: 600 }, padding: 20, usePointStyle: true, pointStyleWidth: 10 } },
      tooltip: { backgroundColor: '#0f172a', titleColor: '#f8fafc', bodyColor: '#cbd5e1', padding: 12, cornerRadius: 10, bodyFont: { size: 13 }, boxPadding: 6 }
    }
  };

  // Bar chart - Portal comparison
  const barData = {
    labels: portalStats.map(p => p.name?.substring(0, 12) || 'Portal'),
    datasets: [
      { label: 'Users', data: portalStats.map(p => p.userCount || 0), backgroundColor: 'rgba(126,165,46,0.8)', borderRadius: 8, barPercentage: 0.6 },
      { label: 'Campaigns', data: portalStats.map(p => p.campaignCount || 0), backgroundColor: 'rgba(34,197,94,0.8)', borderRadius: 8, barPercentage: 0.6 }
    ]
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { labels: { color: '#475569', font: { family: 'Inter, sans-serif', size: 12, weight: 600 } } },
      tooltip: { backgroundColor: '#0f172a', titleColor: '#f8fafc', bodyColor: '#cbd5e1', padding: 12, cornerRadius: 10, bodyFont: { size: 13 }, boxPadding: 6 }
    },
    scales: {
      x: { ticks: { color: '#94a3b8', font: { size: 11 } }, grid: { display: false } },
      y: { ticks: { color: '#94a3b8', font: { size: 11 } }, grid: { color: 'rgba(226,232,240,0.5)', drawBorder: false }, beginAtZero: true }
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Command Center</h1>
          <p className="page-subtitle">System-wide overview</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon green"><FiLayers /></div>
          <div className="stat-card-value">{stats?.totalPortals || 0}</div>
          <div className="stat-card-label">Active Workspaces</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon blue"><FiUsers /></div>
          <div className="stat-card-value">{stats?.totalUsers || 0}</div>
          <div className="stat-card-label">Total People</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon purple"><FiSend /></div>
          <div className="stat-card-value">{stats?.totalCampaigns || 0}</div>
          <div className="stat-card-label">Total Broadcasts</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon cyan"><FiMessageCircle /></div>
          <div className="stat-card-value">{(stats?.totalMessages || 0).toLocaleString()}</div>
          <div className="stat-card-label">Total Messages</div>
        </div>
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="chart-container">
          <h3 className="card-title" style={{ marginBottom: 16 }}>Workspace Status</h3>
          <Doughnut data={doughnutData} options={doughnutOptions} />
        </div>

        <div className="chart-container">
          <h3 className="card-title" style={{ marginBottom: 16 }}>Workspace Comparison</h3>
          {portalStats.length > 0 ? (
            <Bar data={barData} options={barOptions} />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📊</div>
              <h3>No workspaces yet</h3>
              <p>Workspace metrics will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Portal Breakdown Table */}
      {portalStats.length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 16 }}>Workspace Details</h3>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Workspace</th><th>Domain</th><th>Users</th><th>Campaigns</th><th>Status</th></tr>
              </thead>
              <tbody>
                {portalStats.map(p => (
                  <tr key={p._id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</td>
                    <td>{p.domain || '-'}</td>
                    <td>{p.userCount || 0}</td>
                    <td>{p.campaignCount || 0}</td>
                    <td><span className={`badge badge-${p.status}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
